import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  CelebrationOutlined as CelebrationIcon,
  CardGiftcardOutlined as GiftIcon,
  ArrowForwardRounded as ArrowIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiService';
import TermsPrivacyModals from '../Modals/TermsPrivacyModals';

interface SocialProfileCompletionModalProps {
  open: boolean;
  onComplete: (updatedUser: any) => void;
}

const SocialProfileCompletionModal: React.FC<SocialProfileCompletionModalProps> = ({ open, onComplete }) => {
  const { t } = useTranslation();
  const { updateUser } = useAuth();

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  const handleComplete = async () => {
    setError('');

    if (!acceptedTerms) {
      setError(t('registerPage.termsRequired', '⚠️ Action Required: Please agree to the Terms of Service and Privacy Policy to proceed.'));
      return;
    }

    setLoading(true);
    try {
      const updatePayload: Record<string, string> = {};
      if (referralCode.trim()) {
        updatePayload.referralCode = referralCode.trim().toUpperCase();
      }

      const response = await apiService.patch('/auth/profile', updatePayload);
      if (response.success && response.data?.user) {
        onComplete(response.data.user);
      } else {
        // Fallback if structure is slightly different
        onComplete((response as any).data || response);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
        t('socialProfileCompletion.updateFailed', 'Could not save your profile. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={() => {}} // prevent dismiss
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, #0F0C29 0%, #302B63 60%, #24243E 100%)',
          border: '1px solid rgba(167, 139, 250, 0.2)',
          boxShadow: '0 25px 80px rgba(124, 58, 237, 0.4)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Top accent bar */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #7C3AED, #A78BFA, #7C3AED)' }} />

      <DialogContent sx={{ px: 4, py: 4 }}>
        {/* Icon + Title */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(124, 58, 237, 0.25)',
              border: '2px solid rgba(167, 139, 250, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <CelebrationIcon sx={{ fontSize: 38, color: '#A78BFA' }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}
          >
            {t('socialProfileCompletion.title', 'One Last Step! 🎉')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
            {t('socialProfileCompletion.subtitle', 'Help us personalize your Ride Club experience')}
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2.5,
              borderRadius: 2,
              bgcolor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#FCA5A5',
              '& .MuiAlert-icon': { color: '#F87171' },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Age Confirmation & Terms checkbox */}
        <Box sx={{ mb: 2.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  if (e.target.checked) {
                    setError('');
                  }
                }}
                sx={{
                  color: error ? '#F87171' : 'rgba(255,255,255,0.5)',
                  '&.Mui-checked': { color: '#A78BFA' },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: error ? '#FCA5A5' : 'rgba(255,255,255,0.7)',
                }}
              >
                {t('registerPage.ageConfirmation', 'I confirm that I am legally recognized as an adult where I live and agree to Ride Club\'s')}{' '}
                <Link
                  component="button"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setTermsModalOpen(true);
                  }}
                  sx={{
                    color: '#A78BFA',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('common.terms', 'Terms of Service')}
                </Link>{' '}
                {t('common.and', 'and')}{' '}
                <Link
                  component="button"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setPrivacyModalOpen(true);
                  }}
                  sx={{
                    color: '#A78BFA',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('common.privacy', 'Privacy Policy')}
                </Link>
                {'.'}
              </Typography>
            }
          />
        </Box>

        {/* Referral Code */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 700,
              letterSpacing: '0.12em',
              mb: 1,
            }}
          >
            {t('register.referralCode', 'REFERRAL CODE').toUpperCase()}
            {' '}
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400, letterSpacing: 0 }}>
              ({t('common.optional', 'Optional')})
            </Box>
          </Typography>
          <TextField
            fullWidth
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            placeholder={t('register.referralPlaceholder', 'e.g. JO-A1B2C3')}
            inputProps={{ maxLength: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GiftIcon sx={{ color: referralCode ? '#A78BFA' : 'rgba(255,255,255,0.35)', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.07)',
                borderRadius: 2,
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                '&:hover fieldset': { borderColor: 'rgba(167,139,250,0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#A78BFA' },
              },
              '& input::placeholder': { color: 'rgba(255,255,255,0.25)' },
            }}
          />
        </Box>

        {/* Continue Button */}
        <Button
          fullWidth
          variant="contained"
          disabled={!acceptedTerms || loading}
          onClick={handleComplete}
          endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowIcon />}
          sx={{
            py: 1.75,
            borderRadius: 2.5,
            fontSize: '0.95rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            background: 'linear-gradient(90deg, #7C3AED, #A78BFA)',
            boxShadow: '0 8px 30px rgba(124, 58, 237, 0.5)',
            '&:hover': {
              background: 'linear-gradient(90deg, #6D28D9, #9370DB)',
              boxShadow: '0 10px 35px rgba(124, 58, 237, 0.65)',
            },
            '&.Mui-disabled': {
              background: 'rgba(124, 58, 237, 0.3)',
              color: 'rgba(255,255,255,0.4)',
            },
          }}
        >
          {loading
            ? t('common.saving', 'Saving...')
            : t('socialProfileCompletion.continue', 'Enter Ride Club')}
        </Button>

      </DialogContent>
      <TermsPrivacyModals
        termsOpen={termsModalOpen}
        privacyOpen={privacyModalOpen}
        onTermsClose={() => setTermsModalOpen(false)}
        onPrivacyClose={() => setPrivacyModalOpen(false)}
      />
    </Dialog>
  );
};

export default SocialProfileCompletionModal;
